// src/pages/Center Admin/Nurse3DashboardApi.tsx
import React, { useEffect, useState } from "react";
import Nurse3DashboardPage from "../Nurse1/Nurse1DashboardPage"; // kept as-is per your structure
import api from "../../../api/axiosConfig";
import "../../../center admin css/centerAdmin.css";

/* ---------------- Local types (no reuse from WelcomeScreen) ---------------- */

export interface UserProfile {
  _id: string;
  displayName: string;
  email: string;
  centerAssignments?: { centerId: string }[];
}

export interface IUserRef {
  userId: string;
  userName: string;
}
export interface IStationRef {
  stationId: string;
  stationName: string;
  users: IUserRef[];
}
export interface ICenterConfig {
  _id: string;
  appointmentClusterId: string;
  centerId: string;
  centerName: string;
  stations: IStationRef[];
}

export interface IScheduleDetails {
  slotDesigns: { [weekday: string]: string };
  totalSlots: number;
  screeningStartDate: string; // DD-MM-YYYY
  screeningEndDate: string; // DD-MM-YYYY
  totalScreeningDays: number; // computed if backend omits it
}

export interface IScreeningCounts {
  totalBookingsToday: number;
  totalCompletedToday: number;
  totalPendingToday: number;
}

/* ---------------- Helpers ---------------- */

function getCenterIdFromConfig(): string {
  try {
    const rawConfig = sessionStorage.getItem("configResponse");

    if (!rawConfig) return "";

    const parsedConfig = JSON.parse(rawConfig);

    return parsedConfig?.data?.[0]?.centerId || "";
  } catch (error) {
    console.error("Failed to read configResponse:", error);
    return "";
  }
}

function readCachedUser(): UserProfile | null {
  const keys = ["backendResponse", "CurrrentResponse", "CurrentResponse"];
  for (const k of keys) {
    try {
      const raw = sessionStorage.getItem(k);
      if (raw) return JSON.parse(raw) as UserProfile;
    } catch {
      // ignore parse errors and try next key
    }
  }
  return null;
}

/** Small utility to wait for a condition (with timeout) */
async function waitFor(
  predicate: () => boolean,
  { attempts = 30, intervalMs = 100 } = {},
): Promise<boolean> {
  for (let i = 0; i < attempts; i++) {
    if (predicate()) return true;
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return predicate();
}

/* ---------------- Component ---------------- */

const Nurse3DashboardApi: React.FC = () => {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [centerConfig, setCenterConfig] = useState<ICenterConfig | null>(null);
  const [scheduleDetails, setScheduleDetails] =
    useState<IScheduleDetails | null>(null);
  const [screeningCounts, setScreeningCounts] =
    useState<IScreeningCounts | null>(null);
  // const [totalScreeningDays, setTotalScreeningDays] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let aborted = false;

    const run = async () => {
      setLoading(true);
      setError(null);

      try {
        // ---- Wait until MainApp has written cache + token ----
        console.info("[Nurse3] Waiting for cached user & token…");
        await waitFor(() => !!readCachedUser());
        await waitFor(() => !!sessionStorage.getItem("accessToken"));
        if (aborted) return;

        const cached = readCachedUser();
        if (!cached) throw new Error("Cached user not found.");
        setUserProfile(cached);
        console.info("[Nurse3] Cached user ready:", cached.displayName);

        const token = sessionStorage.getItem("accessToken");
        if (!token) throw new Error("No access token found.");
        const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

        // ---- Resolve centerId from configResponse ----
        const assignedCenterId = getCenterIdFromConfig();

        if (!assignedCenterId) {
          throw new Error("No centerId found in configResponse.");
        }

        console.info("[Nurse1] Using centerId:", assignedCenterId);

        // ---- Center config (dynamic centerId) ----
        const centerCfgUrl = `/api/v1/center/${assignedCenterId}/config`;
        console.info("[Nurse3] GET", centerCfgUrl);
        // const centerRes = await api.center_api.get<{ data: ICenterConfig }>(
        //   centerCfgUrl,
        //   authHeaders,
        // );
        // const cfg = centerRes.data?.data;
        // if (!cfg?.centerId) throw new Error("Center config missing centerId.");
        // setCenterConfig(cfg);

        const centerRes = await api.center_api.get<{
          data: ICenterConfig | ICenterConfig[];
        }>(centerCfgUrl, authHeaders);

        const rawCenterConfig = centerRes.data?.data;

        const cfg = Array.isArray(rawCenterConfig)
          ? rawCenterConfig[0]
          : rawCenterConfig;

        console.info("[Nurse2] Center config response:", centerRes.data);
        console.info("[Nurse2] Extracted center config:", cfg);

        if (!cfg?.centerId) {
          throw new Error("Center config missing centerId.");
        }

        setCenterConfig(cfg);

        // ---- Schedule details (dynamic centerId) ----
        const scheduleUrl = `/api/appointments/screening/details/${cfg.centerId}`;
        console.info("[Nurse3] GET", scheduleUrl);
        const scheduleRes = await api.appointment_api.get<IScheduleDetails>(
          scheduleUrl,
          authHeaders,
        );
        setScheduleDetails(scheduleRes.data);
        // const scheduleRes = await api.appointment_api.get<Partial<IScheduleDetails>>(
        //   scheduleUrl,
        //   authHeaders
        // );
        // const sched = normalizeSchedule(scheduleRes.data || {});
        // setScheduleDetails(sched);

        // ---- Screening counts (dynamic centerId) ----
        const countsUrl = `/api/appointments/screening/counts/${cfg.centerId}`;
        console.info("[Nurse3] GET", countsUrl);
        const countsRes = await api.appointment_api.get<IScreeningCounts>(
          countsUrl,
          authHeaders,
        );
        setScreeningCounts(countsRes.data);

        // ---- Compute total weekdays (from normalized schedule) ----
        // setTotalScreeningDays(sched.totalScreeningDays);
      } catch (err: any) {
        console.error("Nurse3DashboardApi error:", err);
        setError("Could not load data.");
      } finally {
        if (!aborted) setLoading(false);
      }
    };

    run();

    return () => {
      aborted = true;
    };
  }, []);

  const displayName = userProfile
    ? userProfile.displayName
    : loading
      ? "..."
      : "Guest";

  return (
    <Nurse3DashboardPage
      userName={displayName}
      isLoading={loading}
      error={error}
      centerConfig={centerConfig}
      scheduleDetails={scheduleDetails}
      screeningCounts={screeningCounts}
    />
  );
};

export default Nurse3DashboardApi;
